import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Lock, Mail, AlertCircle } from 'lucide-react';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
        data-testid="admin-login-page"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            ARMAN <span className="text-gold">Admin</span>
          </h1>
          <p className="text-white/60">Sign in to manage your website</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-xl p-8 border border-white/10">
          {error && (
            <div className="flex items-center gap-2 text-red-400 bg-red-400/10 p-3 mb-6">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute start-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
                className="w-full bg-white/5 border border-white/10 text-white ps-12 pe-4 py-3 focus:border-gold outline-none"
                placeholder="admin@armankitchens.com"
              />
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-white/60 text-sm mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute start-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
                className="w-full bg-white/5 border border-white/10 text-white ps-12 pe-4 py-3 focus:border-gold outline-none"
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-gold text-charcoal py-4 font-medium uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin inline-block" />
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-white/40 text-sm mt-6">
          Arman Kitchens &copy; {new Date().getFullYear()}
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
